import { SET_GONTIJO_SESSION } from 'constants/ActionTypes';

const REDACTED = '[Redacted]';

/**
 * The session fields Sentry must never see: the buyer's `client_auth` — a bearer token the ERP
 * accepts until it expires — and the address they signed in with.
 */
const SENSITIVE_SESSION_FIELDS = ['token', 'email'];

/** The redux-form that holds each passenger's identity, filled by hand or by "Viajar como". */
const PASSENGERS_FORM = 'passengers';

/**
 * The passenger fields that identify a person, by leaf name, so `passengers[1].documentId` and
 * `passengers[1].meta.lapChild.documentId` are both caught. The document type, seat and category are
 * left readable: they are what a bug report needs.
 */
const SENSITIVE_PASSENGER_FIELDS = new Set([
  'firstName',
  'secondFirstName',
  'lastName',
  'secondLastName',
  'document',
  'documentId',
  'documentIdSecond',
  'email',
  'phone',
  'dateOfBirth',
  'birthDate',
]);

/**
 * The redux-form actions whose payload is the value typed into `meta.field`. Spelled out rather than
 * read from `redux-form`'s `actionTypes`: this module loads with the store, and suites that mock
 * `redux-form` partially would otherwise fail to import it.
 */
const FIELD_VALUE_ACTIONS = ['@@redux-form/CHANGE', '@@redux-form/AUTOFILL', '@@redux-form/BLUR'];

/**
 * A copy of `values` with every sensitive passenger field masked, at any depth.
 *
 * Copies only the branches it masks something in and returns the very same object otherwise, so the
 * real form state is never touched and a clean form costs no allocation.
 * @param {*} values - Form values, or any branch of them.
 * @returns {*} The masked copy, or `values` itself when nothing in it is sensitive.
 */
const maskPassengerValues = (values) => {
  if (!values || typeof values !== 'object') return values;

  let masked = values;
  Object.keys(values).forEach((key) => {
    const value = values[key];
    const next =
      SENSITIVE_PASSENGER_FIELDS.has(key) && value ? REDACTED : maskPassengerValues(value);
    if (next === value) return;
    if (masked === values) masked = Array.isArray(values) ? [...values] : { ...values };
    masked[key] = next;
  });

  return masked;
};

/**
 * The passengers form as Sentry should see it: its `values` and `initial` masked.
 * @param {object} [form] - `state.form`.
 * @returns {object|undefined} The masked form slice, or `form` itself when nothing was masked.
 */
const redactPassengersForm = (form) => {
  const passengers = form?.[PASSENGERS_FORM];
  if (!passengers) return form;

  const values = maskPassengerValues(passengers.values);
  const initial = maskPassengerValues(passengers.initial);
  if (values === passengers.values && initial === passengers.initial) return form;

  return { ...form, [PASSENGERS_FORM]: { ...passengers, values, initial } };
};

/**
 * Masks the Gontijo token and email inside the session record.
 * @param {object} state - The latest Redux state.
 * @returns {object} The state with the session masked, or `state` itself.
 */
const redactSession = (state) => {
  const session = state.gontijoSession;
  const present = SENSITIVE_SESSION_FIELDS.filter((field) => session?.get?.(field));
  if (!present.length) return state;

  return {
    ...state,
    gontijoSession: present.reduce((masked, field) => masked.set(field, REDACTED), session),
  };
};

/**
 * The Redux state as Sentry should see it.
 *
 * The enhancer attaches the whole state to every event: the Gontijo session holds the buyer's token
 * and email, and the passengers form holds each passenger's name, document, email, phone and birth
 * date. Only those values are masked, and the state is returned untouched when none is set, since
 * this runs on every action.
 * @param {object} state - The latest Redux state.
 * @returns {object|null} The state with the Gontijo session and passenger identities masked.
 */
export const redactState = (state) => {
  if (!state) return null;

  const withSession = redactSession(state);
  const form = redactPassengersForm(withSession.form);
  if (form === withSession.form) return withSession;

  return { ...withSession, form };
};

/**
 * The action as Sentry should record it in the breadcrumbs.
 *
 * Every action lands in the event's breadcrumbs verbatim. `SET_GONTIJO_SESSION` carries the token and
 * email in its record; a redux-form change to a sensitive passenger field — typed, or written by
 * "Viajar como" — carries the value in its payload.
 * @param {object} action - The dispatched action.
 * @returns {object} The action with the Gontijo session or the passenger's value masked.
 */
export const redactAction = (action) => {
  if (FIELD_VALUE_ACTIONS.includes(action?.type)) {
    const isSensitive =
      action.meta?.form === PASSENGERS_FORM &&
      SENSITIVE_PASSENGER_FIELDS.has(String(action.meta.field).split('.').pop()) &&
      Boolean(action.payload);

    return isSensitive ? { ...action, payload: REDACTED } : action;
  }

  if (action?.type !== SET_GONTIJO_SESSION) return action;

  const present = SENSITIVE_SESSION_FIELDS.filter((field) => action.record?.[field]);
  if (!present.length) return action;

  const record = { ...action.record };
  present.forEach((field) => {
    record[field] = REDACTED;
  });

  return { ...action, record };
};
